/**
 * 프로필 수정 다이얼로그
 * 이름, 닉네임, 전화번호 수정
 */

import { useState, useEffect } from "react"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import Grid from "@mui/material/Grid"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDInput from "components/MDInput"
import MDButton from "components/MDButton"
import { request } from "app/network"
import { UserProfile } from "app/types/UserProfile"

interface EditProfileDialogProps {
    open: boolean
    onClose: () => void
    userProfile: UserProfile | null
    onProfileUpdated: () => void
}

function EditProfileDialog({
    open,
    onClose,
    userProfile,
    onProfileUpdated,
}: EditProfileDialogProps): JSX.Element {
    const [formData, setFormData] = useState({
        name: "",
        nickname: "",
        phone: "",
    })
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")

    useEffect(() => {
        if (open && userProfile) {
            setFormData({
                name: userProfile.name || "",
                nickname: userProfile.nickname || "",
                phone: userProfile.phone || "",
            })
            setError("")
        }
    }, [open, userProfile])

    const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData((prev) => ({
            ...prev,
            [field]: e.target.value,
        }))
    }

    const handleSubmit = async () => {
        try {
            setLoading(true)
            setError("")

            const response = await request("User.updateCurrentUser", {
                paramz: {
                    name: formData.name || null,
                    nickname: formData.nickname || null,
                    phone: formData.phone || null,
                },
            })

            if (response.status === 200) {
                onProfileUpdated()
                onClose()
            } else {
                setError("프로필 수정에 실패했습니다.")
            }
        } catch (err: any) {
            console.error("프로필 수정 오류:", err)
            setError(err.response?.data?.message || "프로필 수정 중 오류가 발생했습니다.")
        } finally {
            setLoading(false)
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <MDTypography variant="h5" fontWeight="medium">
                    프로필 수정
                </MDTypography>
            </DialogTitle>

            <DialogContent>
                <MDBox pt={2}>
                    <Grid container spacing={3}>
                        {/* 이름 */}
                        <Grid item xs={12}>
                            <MDInput
                                type="text"
                                label="이름"
                                value={formData.name}
                                onChange={handleChange("name")}
                                fullWidth
                            />
                        </Grid>

                        {/* 닉네임 */}
                        <Grid item xs={12}>
                            <MDInput
                                type="text"
                                label="닉네임"
                                value={formData.nickname}
                                onChange={handleChange("nickname")}
                                fullWidth
                            />
                        </Grid>

                        {/* 전화번호 */}
                        <Grid item xs={12}>
                            <MDInput
                                type="tel"
                                label="전화번호"
                                value={formData.phone}
                                onChange={handleChange("phone")}
                                fullWidth
                                placeholder="010-1234-5678"
                            />
                        </Grid>

                        {/* 에러 메시지 */}
                        {error && (
                            <Grid item xs={12}>
                                <MDTypography variant="caption" color="error">
                                    {error}
                                </MDTypography>
                            </Grid>
                        )}
                    </Grid>
                </MDBox>
            </DialogContent>

            <DialogActions>
                <MDButton onClick={onClose} color="secondary" disabled={loading}>
                    취소
                </MDButton>
                <MDButton onClick={handleSubmit} variant="gradient" color="info" disabled={loading}>
                    {loading ? "저장 중..." : "저장"}
                </MDButton>
            </DialogActions>
        </Dialog>
    )
}

export default EditProfileDialog
